"use client";
import React, { useEffect, useState } from "react";
import { Facebook, Instagram, Loader2, Globe, Mail, Phone, Clock, MapPin, Briefcase } from 'lucide-react';
import { FaXTwitter } from "react-icons/fa6";   // ← This gives the proper X / Twitter logo
import { useContact } from '../context/ContactContext';

const ContactUs = () => {
  const { contactData, loading, error, submitting, submitError, submitSuccess, sendContactMessage, clearSubmitStatus } = useContact();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (formErrors[e.target.name]) {
      setFormErrors((prev) => ({ ...prev, [e.target.name]: '' }));
    }
    if (submitError || submitSuccess) {
      clearSubmitStatus();
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = 'Invalid email';
    if (!form.message.trim()) errors.message = 'Message is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const result = await sendContactMessage(form);
    if (result.success) {
      setForm({ name: "", email: "", message: "" });
      setFormErrors({});
    }
  };

  const getSocialIcons = () => {
    if (!contactData) return [];
    return [
      {
        icon: Facebook,
        name: "Facebook",
        href: contactData.facebook,
        color: "bg-blue-600 hover:bg-blue-700",
      },
      {
        icon: FaXTwitter,           // ← Use the nice X/Twitter icon from react-icons
        name: "X",
        href: contactData.twitter,
        color: "bg-black hover:bg-gray-900", // dark theme fits X branding
      },
      {
        icon: Instagram,
        name: "Instagram",
        href: contactData.instagram,
        color: "bg-gradient-to-bl from-blue-600  via via-pink-600 via-orange-500 to-yellow-400 hover:from-yellow-500 hover:via-red-600 hover:to-purple-700",
      },
    ]
      .map((s) => ({
        ...s,
        href: s.href?.startsWith('http') ? s.href : s.href ? `https://${s.href}` : '',
      }))
      .filter((s) => s.href && s.href !== 'https://');
  };

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen">
        <main className="flex-1 flex items-center justify-center bg-[#fff8ef] py-16">
          <div className="flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
            <span>Loading...</span>
          </div>
        </main>
      </div>
    );
  }

  if (error && !contactData) {
    return (
      <div className="flex flex-col min-h-screen">
        <main className="flex-1 flex items-center justify-center bg-[#fff8ef] py-16 px-4">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
            <p className="text-gray-600">Please try again later.</p>
          </div>
        </main>
      </div>
    );
  }

  const socialIcons = getSocialIcons();

  return (
    <div className="flex flex-col min-h-screen">
      <main className="flex-1 bg-[#fff8ef] px-4 sm:px-6 py-4 md:py-6">
        <div className="max-w-5xl mx-auto space-y-4">
          {/* Title */}
          <header className="text-center">
            <div
            className=" text-3xl sm:text-3xl md:text-3xl lg:text-4xl
      font-bold leading-none text-black
      tracking-[1px] md:tracking-[2px]
      hover:opacity-80 transition-opacity duration-200
      flex-shrink-0 min-w-0

      lg:[-webkit-text-stroke:2px_gray-700]"

            aria-label="Manaprint Home"
          >

              Contact Manaprint
            </div>
            <p className="mt-2 text-sm text-gray-600 max-w-xl mx-auto">
              Questions about prints, orders or design? We're here to help.
            </p>
          </header>


          {/* Card 2 – Form */}
          <div className="bg-white rounded-2xl shadow-md px-5 py-2 sm:p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5 text-center md:text-left">Send us a message</h2>

            {submitSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-center text-sm text-green-800">
                Message sent — we'll reply soon!
              </div>
            )}
            {submitError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-center text-sm text-red-800">
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-800 mb-1.5">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${
                    formErrors.name
                      ? 'border-red-400 focus:ring-red-200 bg-red-50'
                      : 'border-gray-200 focus:ring-blue-200'
                  }`}
                  placeholder="Your name"
                />
                {formErrors.name && <p className="mt-1 text-red-600 text-xs">{formErrors.name}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-800 mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition-all ${
                    formErrors.email
                      ? 'border-red-400 focus:ring-red-200 bg-red-50'
                      : 'border-gray-200 focus:ring-blue-200'
                  }`}
                  placeholder="you@example.com"
                />
                {formErrors.email && <p className="mt-1 text-red-600 text-xs">{formErrors.email}</p>}
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-800 mb-1.5">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className={`w-full border rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 transition-all ${
                    formErrors.message
                      ? 'border-red-400 focus:ring-red-200 bg-red-50'
                      : 'border-gray-200 focus:ring-blue-200'
                  }`}
                  placeholder="Your question or feedback..."
                />
                {formErrors.message && <p className="mt-1 text-red-600 text-xs">{formErrors.message}</p>}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-6 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-60 transition-all hover:scale-105"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    "Send Message"
                  )}
                </button>
                <span className="text-xs text-gray-500 text-center sm:text-right">
                  Reply in 1–2 business days
                </span>
              </div>
            </form>
          </div>

           {/* Card 1 – Get in Touch */}
          <div className="bg-white rounded-2xl shadow-md py-2 px-5 sm:p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-5 text-center md:text-left">Get in Touch</h2>

            {/* Row 1: 4 items */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {contactData?.website && (
                <MiniContactCard
                  icon={Globe}
                  color="blue"
                  title="Website"
                  value={contactData.website.replace(/^https?:\/\//, '')}
                  href={
                    contactData.website.startsWith('http')
                      ? contactData.website
                      : `https://${contactData.website}`
                  }
                />
              )}
              {contactData?.email && (
                <MiniContactCard
                  icon={Mail}
                  color="red"
                  title="Email"
                  value={contactData.email}
                  href={`mailto:${contactData.email}`}
                />
              )}
              {contactData?.phone && (
                <MiniContactCard
                  icon={Phone}
                  color="green"
                  title="Phone"
                  value={contactData.phone}
                  href={`tel:${contactData.phone.replace(/\s+/g, '')}`}
                />
              )}
              {contactData?.hours && (
                <MiniContactCard
                  icon={Clock}
                  color="purple"
                  title="Hours"
                  value={contactData.hours}
                />
              )}
            </div>

            {/* Row 2: Address + Enquiries */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
              {contactData?.address && (
                <div className="h-full">
                  <div className="flex flex-col items-center text-center p-5 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 h-full min-h-[180px] transition-all group-hover:scale-[1.02]">
                    <div className="w-11 h-11 bg-orange-500 rounded-full flex items-center justify-center mb-3">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-sm font-medium text-gray-700 mb-1.5">Address</span>
                    <address className="not-italic text-xs sm:text-sm text-gray-800 font-medium leading-relaxed">
                      {contactData.address}
                    </address>
                  </div>
                </div>
              )}

              {contactData?.enquiries && (
                <div className="h-full">
                  <div className="flex flex-col items-center text-center p-5 rounded-xl bg-gradient-to-br from-indigo-50 to-indigo-100 h-full min-h-[180px] transition-all group-hover:scale-[1.02]">
                    <div className="w-11 h-11 bg-indigo-500 rounded-full flex items-center justify-center mb-3">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1.5">Business Enquiries</h3>
                    <p className="text-xs text-gray-600 mb-2 leading-snug">Bulk / B2B / reseller</p>
                    <a
                      href={`mailto:${contactData.enquiries}`}
                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {contactData.enquiries}
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Social */}
            {socialIcons.length > 0 && (
              <div className="pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 text-center">Follow Us</h3>
                <div className="flex justify-center gap-4">
                  {socialIcons.map((social, i) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={i}
                        href={social.href}
                        className={`group w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${social.color}`}
                        aria-label={social.name}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon size={18} className="text-white" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
};

const MiniContactCard = ({ icon: Icon, color, title, value, href }) => {
  const bg = {
    blue: 'bg-blue-500 hover:bg-blue-600',
    red: 'bg-red-500 hover:bg-red-600',
    green: 'bg-green-500 hover:bg-green-600',
    purple: 'bg-purple-500',
  }[color] || 'bg-gray-500';

  const from = {
    blue: 'from-blue-50',
    red: 'from-red-50',
    green: 'from-green-50',
    purple: 'from-purple-50',
  }[color] || 'from-gray-50';

  const content = (
    <>
      <div className={`w-10 h-10 ${bg} rounded-full flex items-center justify-center mb-2 transition-colors`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className="text-xs font-medium text-gray-600 mb-1">{title}</span>
      <span className="text-xs text-gray-800 font-medium break-all">{value}</span>
    </>
  );

  const cls = `flex flex-col items-center text-center p-4 rounded-xl bg-gradient-to-br ${from} to-${color}-100 transition-all group-hover:scale-105`;

  return (
    <div className="group h-full">
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cls} aria-label={title}>
          {content}
        </a>
      ) : (
        <div className={cls}>{content}</div>
      )}
    </div>
  );
};

export default ContactUs;
